// app/signup/page.tsx
"use client";

import { useState, useEffect, useCallback, useRef, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  ArrowRight,
  Loader2,
  ChefHat,
  ShieldCheck,
  Users,
  Store,
  Briefcase,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { SM_Header } from "@/components/small-nav";
import { useUser } from "@/context/UserContext";
import { useAuthStore } from "@/lib/stores/auth.store";
import { notify } from "@/lib/notification/toast-manager";

// ============================================================================
// USER TYPE CONFIGURATION
// ============================================================================

const USER_TYPES = {
  customer: {
    backendValue: 'customer',
    displayName: 'Customer',
    icon: Users,
    color: "#FF8C42",
    description: 'Order food and services',
    dashboardPath: '/dashboards/customer',
  },
  owner: {
    backendValue: 'owner',
    displayName: 'Restaurant Owner',
    icon: Store,
    color: "#2F4858",
    description: 'Manage restaurant operations',
    dashboardPath: '/dashboards/onboarding/vendor',
  },
  waiter: {
    backendValue: 'waiter',
    displayName: 'Service Staff',
    icon: Briefcase,
    color: "#4CAF50",
    description: 'Handle orders and service',
    dashboardPath: '/dashboards/waiter',
  },
} as const;

type UserType = keyof typeof USER_TYPES;

const SECURITY_CONFIG = {
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 64,
};

const DESIGN_TOKENS = {
  colors: {
    primary: "#2F4858",
    secondary: "#FF8C42",
    accent: "#FFE156",
    success: "#4CAF50",
    error: "#F44336",
    warning: "#FF9800",
    background: "#F5F5F7",
    card: "#FFFFFF",
    text: {
      primary: "#1D1D1F",
      secondary: "#86868B",
      tertiary: "#8E8E93",
    },
    border: {
      light: "#E5E5E7",
      medium: "#D1D1D6",
    },
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function SignupPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { registerUser } = useUser();
  const authUI = useAuthStore();
  
  const formRef = useRef<HTMLFormElement>(null);
  const mounted = useRef(true);
  
  const [formState, setFormState] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    userType: "customer" as UserType,
    termsAccepted: false,
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    phone?: string;
    userType?: string;
    termsAccepted?: string;
  }>({});

  // Effects
  useEffect(() => {
    mounted.current = true;
    
    const redirect = searchParams.get('redirect');
    const message = searchParams.get('message');
    
    if (redirect) {
      sessionStorage.setItem('signupRedirect', redirect);
    }
    
    if (message === 'account_required') {
      notify.info('Authentication required for access');
    }
    
    return () => {
      mounted.current = false;
    };
  }, [router, searchParams]);

  // Validation utilities
  const validateEmailFormat = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePasswordStrength = (password: string): boolean => {
    if (password.length < SECURITY_CONFIG.PASSWORD_MIN_LENGTH) return false;
    if (password.length > SECURITY_CONFIG.PASSWORD_MAX_LENGTH) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    return true;
  };

  // Form handlers
  const validateField = useCallback((field: keyof typeof formState, value: string | boolean): string | null => {
    switch (field) {
      case 'firstName':
        if (!value) return "First name is required";
        if (value.toString().trim().length < 2) return "Minimum 2 characters";
        return null;
      
      case 'lastName':
        if (!value) return "Last name is required";
        if (value.toString().trim().length < 2) return "Minimum 2 characters";
        return null;
      
      case 'email':
        if (!value) return "Email address is required";
        if (!validateEmailFormat(value.toString())) return "Please enter a valid email address";
        return null;
      
      case 'password':
        if (!value) return "Password is required";
        if (!validatePasswordStrength(value.toString())) return "Password must be at least 8 characters with uppercase, lowercase, and number";
        return null;
      
      case 'confirmPassword':
        if (!value) return "Please confirm your password";
        if (value !== formState.password) return "Passwords do not match";
        return null;
      
      case 'phone':
        if (!value) return "Phone number is required";
        if (!isValidPhoneNumber(value.toString())) return "Please enter a valid phone number";
        return null;
      
      case 'termsAccepted':
        if (!value) return "You must accept the terms and conditions";
        return null;
      
      default:
        return null;
    }
  }, [formState.password]);

  const validateForm = useCallback((): boolean => {
    const errors: typeof validationErrors = {};
    let isValid = true;

    Object.keys(formState).forEach((key) => {
      const field = key as keyof typeof formState;
      const value = formState[field];
      const error = validateField(field, value);
      
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  }, [formState, validateField]);

  const handleInputChange = useCallback(
    (field: keyof typeof formState) =>
      (e: React.ChangeEvent<HTMLInputElement> | string | boolean) => {
        const value = typeof e === 'object' && 'target' in e ? e.target.value : e;
        
        setFormState(prev => ({ ...prev, [field]: value }));
        
        if (validationErrors[field]) {
          setValidationErrors(prev => ({ ...prev, [field]: undefined }));
        }
        
        if (authUI.error) authUI.setError(null);
      },
    [validationErrors, authUI]
  );

  const handlePhoneChange = useCallback((phone?: string) => {
    handleInputChange('phone')(phone || '');
  }, [handleInputChange]);

  const handleUserTypeChange = useCallback((type: UserType) => {
    handleInputChange('userType')(type);
  }, [handleInputChange]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!mounted.current) return;
    
    if (authUI.isLoading) {
      notify.warning('Please wait while we process your request');
      return;
    }
    
    if (!validateForm()) {
      const firstError = Object.values(validationErrors).find(Boolean);
      if (firstError) {
        notify.error(firstError);
      }
      return;
    }

    try {
      authUI.setLoading(true);
      
      const registrationData = {
        first_name: formState.firstName.trim(),
        last_name: formState.lastName.trim(),
        email: formState.email.toLowerCase().trim(),
        password: formState.password,
        password_confirmation: formState.confirmPassword,
        phone: formState.phone.trim(),
        user_type: USER_TYPES[formState.userType].backendValue as 'customer' | 'owner' | 'waiter',
        terms_accepted: true,
      };
      
      const notifyId = notify.loading('Creating your account...');
      
      const success = await registerUser(registrationData);
      
      if (success) {
        notify.success('Account created successfully!', {
          id: notifyId,
        });
        
        setIsRedirecting(true);
        
        const storedRedirect = sessionStorage.getItem('signupRedirect');
        if (storedRedirect) {
          sessionStorage.removeItem('signupRedirect');
          setTimeout(() => {
            if (mounted.current) {
              router.replace(storedRedirect);
            }
          }, 1000);
        } else {
          const dashboardPath = USER_TYPES[formState.userType].dashboardPath;
          setTimeout(() => {
            if (mounted.current) {
              router.replace(dashboardPath);
            }
          }, 1000);
        }
        
      } else {
        notify.error(authUI.error || 'Registration failed. Please try again.', {
          id: notifyId,
        });
        
        if (authUI.error?.toLowerCase().includes('email') || authUI.error?.toLowerCase().includes('already exists')) {
          setTimeout(() => {
            router.push(`/login?email=${encodeURIComponent(formState.email)}`);
          }, 1500);
        }
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      notify.error(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      if (mounted.current) {
        authUI.setLoading(false);
      }
    }
  }, [
    formState,
    validateForm,
    authUI,
    registerUser,
    router,
    validationErrors,
  ]);

  // Custom phone input styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .PhoneInput {
        width: 100%;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      }
      
      .PhoneInput input {
        width: 100%;
        padding: 0.875rem 1rem 0.875rem 3.25rem !important;
        font-size: 1rem;
        font-weight: 400;
        color: ${DESIGN_TOKENS.colors.text.primary};
        background-color: #FFFFFF;
        border: 2px solid ${DESIGN_TOKENS.colors.border.light};
        border-radius: 18px;
        outline: none;
        transition: all 0.2s ease;
        font-family: inherit;
      }
      
      .PhoneInput input:focus {
        border-color: ${DESIGN_TOKENS.colors.primary};
        box-shadow: 0 0 0 4px ${DESIGN_TOKENS.colors.primary}15;
      }
      
      .PhoneInput input:disabled {
        background-color: ${DESIGN_TOKENS.colors.background};
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      .PhoneInputCountry {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Loading/Redirect state
  if (isRedirecting) {
    return (
      <div style={{ backgroundColor: DESIGN_TOKENS.colors.background }} className="min-h-screen">
        <SM_Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center mb-8">
              <div 
                className="w-20 h-20 rounded-full p-1"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary}, ${DESIGN_TOKENS.colors.secondary})`,
                }}
              >
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                  <ChefHat size={32} style={{ color: DESIGN_TOKENS.colors.primary }} />
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: DESIGN_TOKENS.colors.text.primary }}>
                Welcome to IndeMenu!
              </h3>
              <p style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="mb-6">
                Your account has been created successfully
              </p>
              <div className="inline-flex items-center gap-3 text-sm" style={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Redirecting to dashboard...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: DESIGN_TOKENS.colors.background }} className="min-h-screen">
      <SM_Header />
      
      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-md md:max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center justify-center gap-3 mb-6">
              <div 
                style={{ 
                  backgroundColor: DESIGN_TOKENS.colors.card,
                  borderRadius: "24px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                }}
                className="p-3"
              >
                <ChefHat size={28} style={{ color: DESIGN_TOKENS.colors.primary }} />
              </div>
              <div className="text-left">
                <span 
                  style={{ 
                    color: DESIGN_TOKENS.colors.primary
                  }}
                  className="text-2xl font-bold block"
                >
                  IndeMenu
                </span>
                <span 
                  style={{ 
                    color: DESIGN_TOKENS.colors.text.secondary
                  }}
                  className="text-sm font-medium block mt-0.5"
                >
                  Restaurant Management
                </span>
              </div>
            </div>
            
            <h1 style={{ color: DESIGN_TOKENS.colors.text.primary }} className="text-2xl md:text-3xl font-bold mb-2">
              Create Your Account
            </h1>
            <p style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="text-sm md:text-base">
              Join thousands of restaurant owners and customers
            </p>
          </div>

          {/* Signup Card */}
          <div
            style={{ 
              backgroundColor: DESIGN_TOKENS.colors.card,
              borderRadius: "24px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            }}
            className="p-6 sm:p-8 md:p-10 border border-gray-100"
          >
            <form 
              ref={formRef} 
              onSubmit={handleSubmit}
              className="space-y-6 md:space-y-8" 
              noValidate
            >
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-2.5 md:col-span-1">
                  <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-medium">
                    First Name
                    <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-3.5 top-1/2 transform -translate-y-1/2">
                      <User size={18} />
                    </div>
                    <input
                      type="text"
                      value={formState.firstName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('firstName')(e)}
                      placeholder="Enter Your First Name"
                      disabled={authUI.isLoading}
                      style={{
                        borderColor: validationErrors.firstName ? DESIGN_TOKENS.colors.error : DESIGN_TOKENS.colors.border.light,
                        backgroundColor: authUI.isLoading ? DESIGN_TOKENS.colors.background : DESIGN_TOKENS.colors.card,
                        color: DESIGN_TOKENS.colors.text.primary,
                        borderRadius: "18px",
                        paddingLeft: '3.25rem',
                      }}
                      className="w-full px-4 py-3.5 border-2 outline-none transition-all duration-200 text-base"
                    />
                  </div>
                  {validationErrors.firstName && (
                    <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-xs font-medium ml-1">
                      {validationErrors.firstName}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2.5 md:col-span-1">
                  <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-medium">
                    Last Name
                    <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-3.5 top-1/2 transform -translate-y-1/2">
                      <User size={18} />
                    </div>
                    <input
                      type="text"
                      value={formState.lastName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('lastName')(e)}
                      placeholder="Enter Your Last Name"
                      disabled={authUI.isLoading}
                      style={{
                        borderColor: validationErrors.lastName ? DESIGN_TOKENS.colors.error : DESIGN_TOKENS.colors.border.light,
                        backgroundColor: authUI.isLoading ? DESIGN_TOKENS.colors.background : DESIGN_TOKENS.colors.card,
                        color: DESIGN_TOKENS.colors.text.primary,
                        borderRadius: "18px",
                        paddingLeft: '3.25rem',
                      }}
                      className="w-full px-4 py-3.5 border-2 outline-none transition-all duration-200 text-base"
                    />
                  </div>
                  {validationErrors.lastName && (
                    <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-xs font-medium ml-1">
                      {validationErrors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="space-y-2.5">
                <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-medium">
                  Email Address
                  <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                </label>
                <div className="relative">
                  <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-3.5 top-1/2 transform -translate-y-1/2">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    value={formState.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email')(e)}
                    placeholder="Enter Your Email Address"
                    disabled={authUI.isLoading}
                    style={{
                      borderColor: validationErrors.email ? DESIGN_TOKENS.colors.error : DESIGN_TOKENS.colors.border.light,
                      backgroundColor: authUI.isLoading ? DESIGN_TOKENS.colors.background : DESIGN_TOKENS.colors.card,
                      color: DESIGN_TOKENS.colors.text.primary,
                      borderRadius: "18px",
                      paddingLeft: '3.25rem',
                    }}
                    className="w-full px-4 py-3.5 border-2 outline-none transition-all duration-200 text-base"
                  />
                </div>
                {validationErrors.email && (
                  <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-xs font-medium ml-1">
                    {validationErrors.email}
                  </p>
                )}
              </div>

              {/* Phone Field */}
              <div className="space-y-2.5">
                <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-medium">
                  Phone Number
                  <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                </label>
                <div className="relative">
                  <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-3.5 top-1/2 transform -translate-y-1/2 z-10">
                    <Phone size={18} />
                  </div>
                  
                  <PhoneInput
                    international
                    defaultCountry="MW"
                    value={formState.phone}
                    onChange={handlePhoneChange}
                    disabled={authUI.isLoading}
                    required
                    placeholder="Enter Your phone number"
                    className="w-full"
                  />
                </div>
                {validationErrors.phone && (
                  <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-xs font-medium ml-1">
                    {validationErrors.phone}
                  </p>
                )}
              </div>

              {/* Account Type Selector */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-semibold">
                    Account Type
                    <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                  </label>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100"> 
                    <span style={{ color: DESIGN_TOKENS.colors.primary }} className="text-xs font-medium">
                      Select one
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {(Object.entries(USER_TYPES) as [UserType, typeof USER_TYPES[UserType]][]).map(([key, config]) => {
                    const Icon = config.icon;
                    const isActive = formState.userType === key;
                    
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleUserTypeChange(key)}
                        disabled={authUI.isLoading}
                        className={`
                          flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300
                          ${authUI.isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md active:scale-[0.98]'}
                        `}
                        style={{
                          backgroundColor: isActive ? `${config.color}08` : DESIGN_TOKENS.colors.card,
                          borderColor: isActive ? config.color : DESIGN_TOKENS.colors.border.light,
                        }}
                      >
                        <div 
                          className="flex-shrink-0 p-3 rounded-lg transition-all duration-300"
                          style={{ 
                            backgroundColor: isActive ? config.color : DESIGN_TOKENS.colors.background,
                          }}
                        >
                          <Icon 
                            size={20}
                            style={{ 
                              color: isActive ? DESIGN_TOKENS.colors.card : DESIGN_TOKENS.colors.text.secondary,
                            }}
                          />
                        </div>
                        
                        <div className="flex-1 text-left">
                          <div className="flex items-center justify-between">
                            <span 
                              className="font-semibold block"
                              style={{ 
                                color: isActive ? config.color : DESIGN_TOKENS.colors.text.primary,
                                fontSize: '0.9375rem',
                              }}
                            >
                              {config.displayName}
                            </span>
                            {isActive && (
                              <CheckCircle size={18} style={{ color: config.color }} />
                            )}
                          </div>
                          <span 
                            className="block text-sm mt-0.5"
                            style={{ 
                              color: isActive ? DESIGN_TOKENS.colors.text.secondary : DESIGN_TOKENS.colors.text.tertiary,
                            }}
                          >
                            {config.description}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-2.5 md:col-span-1">
                  <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-medium">
                    Password
                    <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-3.5 top-1/2 transform -translate-y-1/2">
                      <Lock size={18} />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formState.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('password')(e)}
                      placeholder="Create secure password"
                      disabled={authUI.isLoading}
                      style={{
                        borderColor: validationErrors.password ? DESIGN_TOKENS.colors.error : DESIGN_TOKENS.colors.border.light,
                        backgroundColor: authUI.isLoading ? DESIGN_TOKENS.colors.background : DESIGN_TOKENS.colors.card,
                        color: DESIGN_TOKENS.colors.text.primary,
                        borderRadius: "18px",
                        paddingLeft: '3.25rem',
                        paddingRight: '3rem',
                      }}
                      className="w-full px-4 py-3.5 border-2 outline-none transition-all duration-200 text-base"
                    />
                    <button
                      type="button"
                      style={{ color: DESIGN_TOKENS.colors.text.secondary }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 transition-opacity p-1 z-10"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={authUI.isLoading}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-xs font-medium ml-1">
                      {validationErrors.password}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2.5 md:col-span-1">
                  <label style={{ color: DESIGN_TOKENS.colors.text.primary }} className="block text-sm font-medium">
                    Confirm Password
                    <span style={{ color: DESIGN_TOKENS.colors.error }} className="ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <div style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="absolute left-3.5 top-1/2 transform -translate-y-1/2">
                      <Lock size={18} />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formState.confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('confirmPassword')(e)}
                      placeholder="Re-enter password"
                      disabled={authUI.isLoading}
                      style={{
                        borderColor: validationErrors.confirmPassword ? DESIGN_TOKENS.colors.error : DESIGN_TOKENS.colors.border.light,
                        backgroundColor: authUI.isLoading ? DESIGN_TOKENS.colors.background : DESIGN_TOKENS.colors.card,
                        color: DESIGN_TOKENS.colors.text.primary,
                        borderRadius: "18px",
                        paddingLeft: '3.25rem',
                        paddingRight: '3rem',
                      }}
                      className="w-full px-4 py-3.5 border-2 outline-none transition-all duration-200 text-base"
                    />
                    <button
                      type="button"
                      style={{ color: DESIGN_TOKENS.colors.text.secondary }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 transition-opacity p-1 z-10"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={authUI.isLoading}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <p style={{ color: DESIGN_TOKENS.colors.error }} className="text-xs font-medium ml-1">
                      {validationErrors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-3">
                <div className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                  validationErrors.termsAccepted 
                    ? 'border-red-200 bg-red-50' 
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}>
                  <div className="flex items-center h-4 mt-0.5 flex-shrink-0">
                    <input
                      id="termsAccepted"
                      type="checkbox"
                      checked={formState.termsAccepted}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('termsAccepted')(e.target.checked)}
                      disabled={authUI.isLoading}
                      style={{
                        borderColor: formState.termsAccepted ? DESIGN_TOKENS.colors.primary : DESIGN_TOKENS.colors.border.medium,
                        backgroundColor: formState.termsAccepted ? DESIGN_TOKENS.colors.primary : DESIGN_TOKENS.colors.card,
                      }}
                      className="h-4 w-4 rounded border-2 focus:ring-2 focus:ring-offset-0 focus:ring-opacity-20 transition-colors cursor-pointer"
                    />
                  </div>
                  <label htmlFor="termsAccepted" style={{ color: DESIGN_TOKENS.colors.text.primary }} className="text-xs md:text-sm">
                    I agree to the{" "}
                    <Link
                      href="/terms"
                      style={{ color: DESIGN_TOKENS.colors.primary }}
                      className="font-medium hover:underline"
                      target="_blank"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="/privacy"
                      style={{ color: DESIGN_TOKENS.colors.primary }}
                      className="font-medium hover:underline"
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {validationErrors.termsAccepted && (
                  <div className="flex items-center gap-2 text-xs p-3 rounded-lg" 
                    style={{ 
                      backgroundColor: `${DESIGN_TOKENS.colors.error}15`,
                      border: `1px solid ${DESIGN_TOKENS.colors.error}30`,
                    }}>
                    <AlertTriangle size={14} style={{ color: DESIGN_TOKENS.colors.error }} />
                    <span style={{ color: DESIGN_TOKENS.colors.error }} className="font-medium">
                      {validationErrors.termsAccepted}
                    </span>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2 md:pt-4">
                <button
                  type="submit"
                  disabled={authUI.isLoading}
                  style={{ 
                    backgroundColor: DESIGN_TOKENS.colors.primary,
                    borderRadius: "18px",
                  }}
                  className={`
                    w-full py-3.5 px-6 font-semibold text-white text-base
                    transition-all duration-200 flex items-center justify-center gap-2
                    ${authUI.isLoading
                      ? 'opacity-60 cursor-not-allowed' 
                      : 'hover:opacity-90 active:opacity-80 active:scale-[0.99]'
                    }
                    md:py-4 md:px-8 md:text-lg
                  `}
                >
                  {authUI.isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Creating Account...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Account</span>
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="flex items-center my-6 md:my-8">
              <div className="flex-1 h-px" style={{ backgroundColor: DESIGN_TOKENS.colors.border.light }} />
              <span style={{ color: DESIGN_TOKENS.colors.text.tertiary }} className="px-3 text-xs md:text-sm font-medium">
                Already have an account?
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: DESIGN_TOKENS.colors.border.light }} />
            </div>

            {/* Login Link */}
            <Link
              href="/login"
              style={{ 
                borderColor: DESIGN_TOKENS.colors.border.light,
                borderRadius: "18px",
              }}
              className={`
                w-full py-3.5 px-6 font-semibold text-base border-2
                transition-all duration-200 flex items-center justify-center gap-2
                hover:border-opacity-60 active:scale-[0.99]
              `}
            >
              <span style={{ color: DESIGN_TOKENS.colors.primary }}>Sign In to Existing Account</span>
            </Link>

            {/* Security Badge */}
            <div 
              className="mt-6 p-4 rounded-xl flex items-center justify-center gap-2.5 backdrop-blur-sm"
              style={{ 
                backgroundColor: DESIGN_TOKENS.colors.background,
                borderRadius: "18px",
              }}
            >
              <ShieldCheck size={18} style={{ color: DESIGN_TOKENS.colors.success }} />
              <span style={{ color: DESIGN_TOKENS.colors.text.secondary }} className="text-xs font-medium">
                End-to-end encrypted • Secure registration
              </span>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{ color: DESIGN_TOKENS.colors.text.secondary }}
            className="mt-6 md:mt-8 text-center text-xs md:text-sm"
          >
            <div className="space-y-2 md:space-y-3">
              <p>
                By creating an account, you agree to our{' '}
                <Link 
                  href="/terms" 
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="font-medium hover:underline"
                >
                  Terms
                </Link>
                {' '}and{' '}
                <Link 
                  href="/privacy" 
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="font-medium hover:underline"
                >
                  Privacy Policy
                </Link>
              </p> 
              <p className="text-xs md:text-sm" style={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                Need help?{' '}
                <Link 
                  href="/pages/contact" 
                  style={{ color: DESIGN_TOKENS.colors.primary }}
                  className="font-medium hover:underline"
                >
                  Contact Support
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
export default function SignupPage() {
  return (
    <Suspense fallback={null}>
      <SignupPageContent />
    </Suspense>
  );
}
